import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { organizationsListUpdated } from 'modules/organizations/reducers/organizations';
import {
  closeNotification,
  danger,
  resetAll
} from 'redux-store/reducers/common-reducer';

import { fetchCurrentAccount } from './account';
import { receiveRoles } from './receive-roles';
import {
  EVENT_TYPE_LOGIN_ATTEMPT,
  trackEvent,
  trackLogin,
  trackLogout
} from '../../tracking/utils/tracking';

import { API, destroy, post } from 'utils/api';
import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { removeItem } from 'utils/storage/localStorage';
import { TEMPORARY_SESSION_STORAGE_KEY } from 'utils/session';
import {
  redirectToAppStorePage,
  redirectToLoginPage,
  redirectToMainPage,
  shouldRedirectToAppStorePage
} from 'utils/routes';
import {
  API_CODE_CREDENTIALS_INCORRECT,
  API_CODE_DELETION_IS_REQUESTED,
  API_CODE_INACTIVE_USER,
  API_CODE_TOO_MANY_ATTEMPTS
} from 'utils/status';
import {
  decodeToken,
  forgetTokens,
  getAccessToken,
  getRefreshToken,
  setTokens
} from 'utils/token';

applyTranslations(require('../common/access-common.i18n.json'));

const { NEW_SESSIONS } = API.END_POINTS.API;

const msgKey = 'access_common';

function createSession(credentials) {
  const suppressAuth = true;

  const onSuccess = dispatch => response => {
    const tokens = response.data?.token ?? response.data;
    const roles = decodeToken(tokens.accessToken)?.roles ?? [];

    setTokens(tokens);
    receiveRoles(roles);
    dispatch(hideLoading(NEW_SESSIONS));
  };

  return (dispatch) => {
    dispatch(showLoading(NEW_SESSIONS));

    return post(NEW_SESSIONS, { data: credentials }, { suppressAuth })
      .then(onSuccess(dispatch))
      .catch((error) => {
        const { response, message } = error;

        if (response && response.data) {
          const { code, title } = response.data;
          const requestEmail = `<a href="/users/request-activation-email">
            ${__t(`${msgKey}.messages.click_here_to_resend_activation_email`)}</a>
          `;
          const statusMessages = {
            [API_CODE_INACTIVE_USER]: `${__t(`${msgKey}.messages.user_account_not_active`)} ${requestEmail}`,
            [API_CODE_CREDENTIALS_INCORRECT]: __t(`${msgKey}.messages.email_or_password_incorrect`),
            [API_CODE_DELETION_IS_REQUESTED]: __t(`${msgKey}.messages.email_or_password_incorrect`),
            [API_CODE_TOO_MANY_ATTEMPTS]: __t(`${msgKey}.messages.too_many_login_attempts`)
          };

          dispatch(danger(statusMessages[code] || title));
        } else {
          dispatch(danger(message));
        }

        dispatch(hideLoading(NEW_SESSIONS));
        logError(error);

        throw error; // don't suppress error
      });
  };
}

function removeSession(isSessionTerminated = false) {
  return (dispatch) => {
    if (isSessionTerminated) {
      return Promise.resolve(forgetTokens());
    }

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    const decodedAccessToken = decodeToken(accessToken);

    if (!accessToken || !decodedAccessToken || !decodedAccessToken?.session_id || !refreshToken) {
      return Promise.resolve(forgetTokens());
    }

    const url = NEW_SESSIONS;
    const data = {
      refreshToken
    };

    dispatch(showLoading(NEW_SESSIONS));

    return destroy(url, { data }, { isRepeatingRequest: true })
      .then(() => dispatch(hideLoading(NEW_SESSIONS)))
      .catch(() => dispatch(hideLoading(NEW_SESSIONS)))
      .then(() => forgetTokens());
  };
}

export function login(data) {
  const { email, password } = data;

  return (dispatch, getState) => {
    dispatch(closeNotification());
    trackEvent(EVENT_TYPE_LOGIN_ATTEMPT);

    const credentials = { email, password, permanent: true }; // `permanent` could be set via "Remember me" checkbox

    return dispatch(createSession(credentials))
      .then(() => trackLogin(email))
      .then(() => (
        dispatch(fetchCurrentAccount())
          .then(account => dispatch(organizationsListUpdated(account.organizations)))
      ))
      .then(() => redirectToMainPage(getState()));
  };
}

export function logout(options = {}) {
  const { isSessionTerminated } = options;

  return (dispatch) => dispatch(removeSession(isSessionTerminated))
    .then(() => dispatch(resetAll()))
    .then(() => trackLogout())
    .then(() => removeItem(TEMPORARY_SESSION_STORAGE_KEY));
}

export function terminateSession() {
  return (dispatch) => dispatch(logout({ isSessionTerminated: true }))
    .then(() => {
      const location = window.currentLocation;
      const { pathname: pointBackTo } = location;

      if (shouldRedirectToAppStorePage(location)) {
        redirectToAppStorePage();
        return;
      }

      redirectToLoginPage(pointBackTo);
    });
}

export function restoreSession() {
  return (dispatch) => {
    dispatch(showLoading('restoreSession()'));

    return dispatch(fetchCurrentAccount())
      .then(account => {
        dispatch(organizationsListUpdated(account.organizations));
        return dispatch(hideLoading('restoreSession()'));
      })
      .catch(() => dispatch(terminateSession()));
  };
}
